import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './WordleGame.css';
import './CrosswordGame.css';
import { generateLayout } from './crosswordlayout';
import { PointsStarIcon } from './WordleGame';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import DayQuestionPanel from './DayQuestionPanel';
import { useSelector } from 'react-redux';
import { demoGatePopupAndRedirect, isUserDemoBypass } from '../../utils/demoGate';

function buildLayoutWords(questions) {
  const list = Array.isArray(questions) ? questions : [];
  return [...list]
    .filter((q) => String(q?.answer ?? '').trim().length >= 2)
    .sort(
      (a, b) =>
        String(b.answer).replace(/\s/g, '').length -
        String(a.answer).replace(/\s/g, '').length
    )
    .map((q) => ({
      answer: String(q.answer).replace(/\s/g, '').toUpperCase(),
      question: String(q.question ?? ''),
      did_you_know: String(q.did_you_know ?? ''),
    }));
}

function initUserGrid(table) {
  return table.map((row) =>
    row.map((c) => (c === '-' ? null : ''))
  );
}

function allFilled(userGrid, solutionTable) {
  for (let i = 0; i < solutionTable.length; i++) {
    for (let j = 0; j < solutionTable[i].length; j++) {
      if (solutionTable[i][j] === '-') continue;
      if (String(userGrid[i][j] ?? '').trim() === '') return false;
    }
  }
  return true;
}

function allMatch(userGrid, solutionTable) {
  for (let i = 0; i < solutionTable.length; i++) {
    for (let j = 0; j < solutionTable[i].length; j++) {
      const sol = solutionTable[i][j];
      if (sol === '-') continue;
      if (String(userGrid[i][j] ?? '').toUpperCase() !== String(sol).toUpperCase()) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Crossword from theme `questions`: { question, answer, did_you_know }.
 * Layout from crosswordlayout.js; same chrome as Day 1 / Day 2.
 */
export default function CrosswordGame({
  questions,
  rules,
  badgeUrl,
  badgeTitle = '',
  questionImage,
  didYouKnowImage,
  correctPoints,
  dayBonusPoints,
  pointsIconUrl,
  badgeFallbackLabel = 'Day 3',
  onComplete,
}) {
  const user = useSelector((state) => state.auth?.user);
  const demoPromoScheduledRef = useRef(false);
  const rulesList = Array.isArray(rules) ? rules : [];
  const questionImageUrl = s3PublicUrl(questionImage);
  const didYouKnowImageUrl = s3PublicUrl(didYouKnowImage);

  const layoutPayload = useMemo(() => {
    const words = buildLayoutWords(questions);
    if (!words.length) return { error: 'No valid words in theme.' };
    try {
      const layout = generateLayout(words);
      const placed = (layout.result || []).filter(
        (w) => w.orientation && w.orientation !== 'none' && w.startx != null
      );
      if (!placed.length) {
        return { error: 'Could not place words in the grid. Try fewer or shorter words.' };
      }
      return { layout, placed };
    } catch (e) {
      return { error: 'Could not build crossword layout.' };
    }
  }, [questions]);

  const solutionTable = layoutPayload.layout?.table;
  const [userGrid, setUserGrid] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [showDidYouKnow, setShowDidYouKnow] = useState(false);
  const [didYouKnowText, setDidYouKnowText] = useState('');
  const [revealAnswer, setRevealAnswer] = useState('');
  const [earnedPoints, setEarnedPoints] = useState(0);
  const [message, setMessage] = useState('');
  const [won, setWon] = useState(false);
  const [badgeImageFailed, setBadgeImageFailed] = useState(false);
  const [pendingCompletePayload, setPendingCompletePayload] = useState(null);
  const completeSentRef = useRef(false);
  const [pointsToast, setPointsToast] = useState(null);
  const inputRefs = useRef(new Map());
  const [activeDir, setActiveDir] = useState('across'); // 'across' | 'down'
  const [solvedWordKeys, setSolvedWordKeys] = useState(() => new Set());

  useEffect(() => {
    setBadgeImageFailed(false);
  }, [badgeUrl]);

  useEffect(() => {
    if (solutionTable) {
      setUserGrid(initUserGrid(solutionTable));
      setMessage('');
      setWon(false);
      setEarnedPoints(0);
      setSolvedWordKeys(new Set());
    }
  }, [solutionTable]);

  const pointsAward = useMemo(() => {
    const n = Number(correctPoints);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [correctPoints]);

  const dayBonusAward = useMemo(() => {
    const n = Number(dayBonusPoints);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }, [dayBonusPoints]);

  const clueNumbers = useMemo(() => {
    if (!layoutPayload.placed || !solutionTable) return {};
    const map = {};
    for (const w of layoutPayload.placed) {
      const r = w.starty - 1;
      const c = w.startx - 1;
      const key = `${r}-${c}`;
      const pos = w.position;
      if (map[key] == null || pos < map[key]) map[key] = pos;
    }
    return map;
  }, [layoutPayload, solutionTable]);

  const cellDirs = useMemo(() => {
    // For each cell key "r-c", track whether it participates in across/down words.
    const map = new Map();
    const placed = layoutPayload.placed || [];
    for (const w of placed) {
      const len = String(w.answer || '').length;
      if (!len) continue;
      const r0 = w.starty - 1;
      const c0 = w.startx - 1;
      for (let k = 0; k < len; k++) {
        const r = w.orientation === 'down' ? r0 + k : r0;
        const c = w.orientation === 'across' ? c0 + k : c0;
        const key = `${r}-${c}`;
        const prev = map.get(key) || { across: false, down: false };
        map.set(key, {
          across: prev.across || w.orientation === 'across',
          down: prev.down || w.orientation === 'down',
        });
      }
    }
    return map;
  }, [layoutPayload]);

  const cluesAcross = useMemo(() => {
    if (!layoutPayload.placed) return [];
    return layoutPayload.placed
      .filter((w) => w.orientation === 'across')
      .sort((a, b) => a.position - b.position);
  }, [layoutPayload]);

  const cluesDown = useMemo(() => {
    if (!layoutPayload.placed) return [];
    return layoutPayload.placed
      .filter((w) => w.orientation === 'down')
      .sort((a, b) => a.position - b.position);
  }, [layoutPayload]);

  const placedWords = useMemo(() => layoutPayload.placed || [], [layoutPayload.placed]);

  const wordCells = useMemo(() => {
    const out = new Map();
    for (const w of placedWords) {
      const len = String(w.answer || '').length;
      const r0 = w.starty - 1;
      const c0 = w.startx - 1;
      const coords = [];
      for (let k = 0; k < len; k++) {
        const r = w.orientation === 'down' ? r0 + k : r0;
        const c = w.orientation === 'across' ? c0 + k : c0;
        coords.push([r, c]);
      }
      out.set(`${w.orientation}-${w.position}-${w.answer}`, coords);
    }
    return out;
  }, [placedWords]);

  const lockedCells = useMemo(() => {
    const out = new Set();
    for (const key of solvedWordKeys) {
      const coords = wordCells.get(key) || [];
      for (const [r, c] of coords) out.add(`${r}-${c}`);
    }
    return out;
  }, [solvedWordKeys, wordCells]);

  const setCell = useCallback(
    (row, col, val) => {
      if (won || !solutionTable) return;
      if (lockedCells.has(`${row}-${col}`)) return;
      const ch = String(val).replace(/[^a-zA-Z0-9]/g, '').slice(-1).toUpperCase();
      setUserGrid((g) => {
        if (!g) return g;
        const next = g.map((r) => [...r]);
        if (next[row][col] == null) return g;
        next[row][col] = ch;
        return next;
      });
      setMessage('');
    },
    [solutionTable, won, lockedCells]
  );

  const focusCell = useCallback((row, col) => {
    const key = `${row}-${col}`;
    const el = inputRefs.current.get(key);
    if (el && typeof el.focus === 'function') {
      el.focus();
      // select for quicker overwrite
      if (typeof el.select === 'function') el.select();
    }
  }, []);

  const findNext = useCallback(
    (row, col, dir) => {
      if (!solutionTable) return null;
      const step =
        dir === 'left' ? { dr: 0, dc: -1 } :
        dir === 'right' ? { dr: 0, dc: 1 } :
        dir === 'up' ? { dr: -1, dc: 0 } :
        { dr: 1, dc: 0 }; // down

      let r = row + step.dr;
      let c = col + step.dc;
      while (
        r >= 0 &&
        c >= 0 &&
        r < solutionTable.length &&
        c < (solutionTable[r]?.length || 0)
      ) {
        if (solutionTable[r][c] !== '-') return { row: r, col: c };
        r += step.dr;
        c += step.dc;
      }
      return null;
    },
    [solutionTable]
  );

  const handleInputChange = useCallback(
    (row, col, value) => {
      const ch = String(value).replace(/[^a-zA-Z0-9]/g, '').slice(-1);
      // build next grid so we can evaluate word completion immediately
      if (!userGrid || !solutionTable || won) return;
      if (lockedCells.has(`${row}-${col}`)) return;
      const nextGrid = userGrid.map((r) => [...r]);
      if (nextGrid[row]?.[col] == null) return;
      nextGrid[row][col] = ch ? ch.toUpperCase() : '';
      setUserGrid(nextGrid);
      if (ch) {
        const next =
          activeDir === 'down'
            ? findNext(row, col, 'down')
            : findNext(row, col, 'right');
        if (next) {
          // Let React commit the value first
          window.setTimeout(() => focusCell(next.row, next.col), 0);
        }
      }
      setMessage('');

      // check if any word is now fully correct; if so show its DYK once
      const newlySolved = [];
      for (const w of placedWords) {
        const key = `${w.orientation}-${w.position}-${w.answer}`;
        if (solvedWordKeys.has(key)) continue;
        const coords = wordCells.get(key) || [];
        let ok = coords.length > 0;
        for (const [r, c] of coords) {
          const sol = solutionTable?.[r]?.[c];
          const u = nextGrid?.[r]?.[c];
          if (!sol || sol === '-') {
            ok = false;
            break;
          }
          if (String(u ?? '').toUpperCase() !== String(sol).toUpperCase()) {
            ok = false;
            break;
          }
        }
        if (ok) newlySolved.push({ key, did_you_know: w.did_you_know });
      }

      if (newlySolved.length) {
        setSolvedWordKeys((prev) => {
          const next = new Set(prev);
          for (const s of newlySolved) next.add(s.key);
          return next;
        });
        setEarnedPoints((p) => p + pointsAward * newlySolved.length);
        const dyk = String(newlySolved[0]?.did_you_know ?? '').trim();
        setDidYouKnowText(dyk);

        setShowDidYouKnow(true);
      }

      // if all words solved, go back to main grid
      const totalPlaced = placedWords.length;
      const solvedCount = solvedWordKeys.size + newlySolved.length;
      if (totalPlaced > 0 && solvedCount >= totalPlaced) {
        const finalPoints = earnedPoints + pointsAward * newlySolved.length + dayBonusAward;
        setWon(true);
        setRevealAnswer('');
        if (dayBonusAward > 0) {
          setEarnedPoints(finalPoints);
          setPointsToast(`+${dayBonusAward}`);
          window.setTimeout(() => setPointsToast(null), 2600);
        }
        setPendingCompletePayload({ earnedPoints: finalPoints, answer: 'completed', allCorrect: true });
      }
    },
    [
      userGrid,
      solutionTable,
      won,
      lockedCells,
      activeDir,
      findNext,
      focusCell,
      placedWords,
      solvedWordKeys,
      wordCells,
      pointsAward,
      earnedPoints,
      dayBonusAward,
    ]
  );

  const handleKeyDown = useCallback(
    (e, row, col) => {
      if (won) return;
      if (lockedCells.has(`${row}-${col}`)) return;
      const k = e.key;
      if (k === 'ArrowRight') {
        e.preventDefault();
        setActiveDir('across');
        const next = findNext(row, col, 'right');
        if (next) focusCell(next.row, next.col);
        return;
      }
      if (k === 'ArrowLeft') {
        e.preventDefault();
        setActiveDir('across');
        const prev = findNext(row, col, 'left');
        if (prev) focusCell(prev.row, prev.col);
        return;
      }
      if (k === 'ArrowDown') {
        e.preventDefault();
        setActiveDir('down');
        const next = findNext(row, col, 'down');
        if (next) focusCell(next.row, next.col);
        return;
      }
      if (k === 'ArrowUp') {
        e.preventDefault();
        setActiveDir('down');
        const prev = findNext(row, col, 'up');
        if (prev) focusCell(prev.row, prev.col);
        return;
      }
      if (k === 'Backspace') {
        const curVal = String(userGrid?.[row]?.[col] ?? '');
        // If empty, move left; else let it clear normally
        if (curVal === '') {
          const prev =
            activeDir === 'down'
              ? findNext(row, col, 'up')
              : findNext(row, col, 'left');
          if (prev) {
            e.preventDefault();
            focusCell(prev.row, prev.col);
          }
        }
      }
    },
    [won, userGrid, findNext, focusCell, activeDir, lockedCells]
  );

  const handleSubmit = useCallback(() => {
    if (isUserDemoBypass(user)) {
      if (!demoPromoScheduledRef.current) {
        demoPromoScheduledRef.current = true;
        demoGatePopupAndRedirect();
      }
      return;
    }
    const firstDidYouKnow = String(placedWords.find((w) => String(w?.did_you_know ?? '').trim())?.did_you_know ?? '').trim();
    const answers = placedWords.map((w) => String(w?.answer ?? '').trim()).filter(Boolean).join(', ');
    const isAllCorrect = Boolean(
      userGrid &&
        solutionTable &&
        allFilled(userGrid, solutionTable) &&
        allMatch(userGrid, solutionTable)
    );
    setMessage('');
    setRevealAnswer(answers);
    setDidYouKnowText(firstDidYouKnow);
    const finalPoints = earnedPoints + (isAllCorrect ? dayBonusAward : 0);
    if (isAllCorrect && dayBonusAward > 0) {
      setEarnedPoints(finalPoints);
      setPointsToast(`+${dayBonusAward}`);
      window.setTimeout(() => setPointsToast(null), 2600);
    }
    setPendingCompletePayload({ earnedPoints: finalPoints, answer: 'completed', allCorrect: isAllCorrect });
    setShowDidYouKnow(true);
    setWon(true);
  }, [earnedPoints, placedWords, userGrid, solutionTable, dayBonusAward, user]);

  if (layoutPayload.error) {
    return (
      <p className="wordle-notification" style={{ textAlign: 'center' }}>
        {layoutPayload.error}
      </p>
    );
  }

  if (!solutionTable || !userGrid) {
    return (
      <div className="crossword-loading" aria-hidden>
        Loading puzzle…
      </div>
    );
  }

  return (
    <div className="wordle-shell">
      <div className="wordle-toolbar-strip">
        <div className="wordle-toolbar-end">
          {rulesList.length > 0 ? (
            <button
              type="button"
              className="wordle-icon-btn wordle-icon-rules"
              onClick={() => setShowRules(true)}
              aria-label="How to play"
              title="How to play"
            >
              ?
            </button>
          ) : null}
          <div className="wordle-points-group" title="Points">
            <PointsStarIcon pointsIconUrl={pointsIconUrl} />
            <span className="wordle-points-value">{earnedPoints}</span>
            {pointsToast ? <span className="wordle-points-toast">{pointsToast}</span> : null}
          </div>
        </div>
      </div>

      <div className="wordle-root crossword-root-inner">
        <div className="wordle-badge-header">
          <div className="wordle-badge-header__logo">
            {badgeUrl && !badgeImageFailed ? (
              <img
                src={badgeUrl}
                alt=""
                className="wordle-badge-img"
                onError={() => setBadgeImageFailed(true)}
              />
            ) : (
              <div className="wordle-badge-fallback" aria-hidden>
                {badgeFallbackLabel}
              </div>
            )}
          </div>
          <div className="wordle-badge-header__title">
            <span className="wordle-badge-title">
              {String(badgeTitle || '').trim() || badgeFallbackLabel}
            </span>
          </div>
        </div>

        {questionImageUrl ? (
          <DayQuestionPanel imageUrl={questionImageUrl} question="" />
        ) : null}

        <div className="crossword-layout">
          <div className="crossword-grid-wrap">
            <table className="crossword-table" role="grid">
              <tbody>
                {solutionTable.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => {
                      if (cell === '-') {
                        return (
                          <td key={j} className="crossword-cell crossword-cell--block" aria-hidden />
                        );
                      }
                      const num = clueNumbers[`${i}-${j}`];
                      return (
                        <td key={j} className="crossword-cell crossword-cell--letter">
                          {num != null ? (
                            <span className="crossword-cell-num">{num}</span>
                          ) : null}
                          <input
                            type="text"
                            className="crossword-input"
                            maxLength={1}
                            value={userGrid[i][j] ?? ''}
                            disabled={won || lockedCells.has(`${i}-${j}`)}
                          onChange={(e) => handleInputChange(i, j, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, i, j)}
                          onFocus={() => {
                            const key = `${i}-${j}`;
                            const d = cellDirs.get(key);
                            // If cell only belongs to down, follow down; if only across, follow across.
                            // If intersection, keep current direction.
                            if (d?.down && !d?.across) setActiveDir('down');
                            else if (d?.across && !d?.down) setActiveDir('across');
                          }}
                          ref={(el) => {
                            const key = `${i}-${j}`;
                            if (el) inputRefs.current.set(key, el);
                            else inputRefs.current.delete(key);
                          }}
                            aria-label={num != null ? `Cell ${num}` : 'Letter'}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="crossword-side">
            <div className="crossword-clues">
              <div className="crossword-clues-col">
                <h3 className="crossword-clues-title">Across</h3>
                <ul className="crossword-clues-list">
                  {cluesAcross.map((w) => (
                    <li key={`a-${w.position}-${w.answer}`}>
                      <span className="crossword-clue-num">{w.position}.</span> {w.question}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="crossword-clues-col">
                <h3 className="crossword-clues-title">Down</h3>
                <ul className="crossword-clues-list">
                  {cluesDown.map((w) => (
                    <li key={`d-${w.position}-${w.answer}`}>
                      <span className="crossword-clue-num">{w.position}.</span> {w.question}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="crossword-actions">
              <button
                type="button"
                className="crossword-check-btn"
                onClick={handleSubmit}
                disabled={
                  won ||
                  !userGrid ||
                  !solutionTable ||
                  !allFilled(userGrid, solutionTable)
                }
              >
                Submit
              </button>
            </div>

            <div className="wordle-notification crossword-message">{message}</div>
          </div>
        </div>

        {showRules && rulesList.length > 0 && (
          <div
            className="wordle-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="crossword-rules-title"
          >
            <div className="wordle-modal">
              <h2 id="crossword-rules-title">How to play</h2>
              <ul className="wordle-rules-list">
                {rulesList.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
              <div className="wordle-modal-actions">
                <button type="button" onClick={() => setShowRules(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showDidYouKnow && (
          <div
            className="wordle-modal-overlay wordle-modal-overlay--dyk"
            role="dialog"
            aria-modal="true"
            aria-label="Answer details"
          >
            <div className="wordle-modal wordle-modal--dyk">
              {didYouKnowImageUrl ? (
                <img src={didYouKnowImageUrl} alt="" className="dyk-banner-image" />
              ) : (
                <div className="dyk-banner-fallback">Did you know?</div>
              )}

              <div className="dyk-body-box">
                {revealAnswer ? (
                  <p className="mcq-reveal dyk-reveal">
                  Correct Answer: <strong>{revealAnswer}</strong>
                  </p>
                ) : null}
                {didYouKnowText ? <p className="dyk-text">{didYouKnowText}</p> : null}
              </div>

              <div className="wordle-modal-actions wordle-modal-actions--center">
                <button
                  type="button"
                  onClick={() => {
                    setShowDidYouKnow(false);
                    if (completeSentRef.current) return;
                    if (pendingCompletePayload && typeof onComplete === 'function') {
                      completeSentRef.current = true;
                      onComplete(pendingCompletePayload);
                    }
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
